import { Component, input, output, signal } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { Subscription } from 'rxjs';
import { Column, SetupWizardDynamicTable } from '../../../setup-wizard-dynamic-table/setup-wizard-dynamic-table';
import { NzIconModule } from 'ng-zorro-antd/icon';
import { CommonModule } from '@angular/common';
import { BrowserService } from '../../../../../../../services/browser.service';
import { HeaderColumn } from '../../../setup-wizard-dynamic-table/header-column';
import { RowColumn } from '../../../setup-wizard-dynamic-table/row-column';
import {
	BeforeAfter,
	COURSE_CSV_LABELS,
	SetupWizardBooleanType,
	SetupWizardCourse,
	CourseCsvKeys,
	CourseCsvTemplate,
	EMPTY_COURSE_CSV_TEMPLATE,
	SetupWizardPayload,
} from '@mira-avant/mtlibs';
import { SetupWizardBeforeAfter } from '../../setup-wizard-before-after/setup-wizard-before-after';
import {
	makeStatusColumn,
	SetupWizardBeforeAfterStatus,
} from '../../setup-wizard-before-after-status/setup-wizard-before-after-status';
import { SetupWizard } from '../../../../../../../services/setup-wizard';
import { NzTooltipModule } from 'ng-zorro-antd/tooltip';
import { NzSpinModule } from 'ng-zorro-antd/spin';
import { NzButtonModule } from 'ng-zorro-antd/button';
import { NzPopconfirmModule } from 'ng-zorro-antd/popconfirm';
import { MAX_FILTER_OPTIONS_ALLOWED } from '../../setup-wizard-edit-steps';

const FULL_WIDTH_BREAKPOINT = 1510;

@Component({
	selector: 'app-setup-wizard-courses-table',
	imports: [
		CommonModule,
		SetupWizardDynamicTable,
		NzIconModule,
		RowColumn,
		HeaderColumn,
		SetupWizardBeforeAfter,
		NzTooltipModule,
		NzSpinModule,
		NzButtonModule,
		NzPopconfirmModule,
		SetupWizardBeforeAfterStatus,
	],
	standalone: true,
	templateUrl: './setup-wizard-courses-table.html',
	styleUrl: './setup-wizard-courses-table.scss',
})
export class SetupWizardCoursesTable {
	private clientUuid: string | undefined;
	private sub?: Subscription;
	private resizeSub?: Subscription;
	public loading = signal<boolean>(true);
	public pageSize = input<number>(10);
	public rows = signal<SetupWizardCourse[]>([]);
	readonly rowType = null! as BeforeAfter<CourseCsvTemplate>; // type inference only
	public columns = signal<Column[]>([]);
	private wideColumns: Column[] = [];
	private narrowColumns: Column[] = [];
	public onEdit = output<SetupWizardCourse>();
	public onToggleDisable = output<SetupWizardCourse>();
	public onRemove = output<SetupWizardCourse>();
	public onReset = output<SetupWizardCourse>();
	public enableEditButton = input<boolean>(true);
	public enableToggleDisableButton = input<boolean>(true);
	public enableRemoveButton = input<boolean>(true);
	public enableResetButton = input<boolean>(true);
	public selectedRow = input<BeforeAfter<CourseCsvTemplate> | null>(null);
	public CourseCsvKeys = CourseCsvKeys;
	public COURSE_CSV_LABELS = COURSE_CSV_LABELS;
	public SetupWizardBooleanType = SetupWizardBooleanType;

	private ssoCourseIdFilterOptions: { text: string; value: string }[] = [];
	private displayOrderFilterOptions: { text: string; value: number }[] = [];
	private enNameFilterOptions: { text: string; value: string }[] = [];

	private ssoCourseIdColumn: Column | undefined;
	private defaultLangIdColumn: Column | undefined;
	private displayOrderColumn: Column | undefined;
	private rubricCodeColumn: Column | undefined;
	private productNameColumn: Column | undefined;
	private enNameColumn: Column | undefined;
	private activeColumn: Column | undefined;

	constructor(
		private route: ActivatedRoute,
		private browserService: BrowserService,
		private setupWizard: SetupWizard
	) {
		this.sub = this.route.params.subscribe(params => {
			this.clientUuid = params['clientUuid'];
		});
	}

	async ngOnInit() {
		await this.initTable();

		this.sub?.add(
			this.setupWizard.preview$.subscribe(payload => {
				this.loading.set(true);

				if (payload) {
					this.updateRows(payload);
				}

				this.loading.set(false);
			})
		);
	}

	private updateRows(payload: SetupWizardPayload) {
		this.rows.set(payload.payload.courses);
		this.updateFilterOptions();
	}

	private updateFilterOptions(): void {
		const rows = this.rows();

		this.ssoCourseIdFilterOptions = [...new Set(rows.map(row => row.before?.sso_course_id || ''))]
			.sort()
			.map(value => ({
				text: value,
				value: value,
			}));

		this.displayOrderFilterOptions = [...new Set(rows.map(row => row.before?.display_order || 0))]
			.sort()
			.map(value => ({
				text: value.toString(),
				value: value,
			}));

		this.enNameFilterOptions = [...new Set(rows.map(row => row.before?.en_name || ''))].sort().map(value => ({
			text: value,
			value: value,
		}));

		if (this.ssoCourseIdFilterOptions.length > MAX_FILTER_OPTIONS_ALLOWED) {
			this.ssoCourseIdColumn!.filterFn = undefined;
			this.ssoCourseIdColumn!.filterOptions = undefined;
		} else {
			this.ssoCourseIdColumn!.filterOptions = this.ssoCourseIdFilterOptions;
			this.ssoCourseIdColumn!.filterFn = (list: string[], item: SetupWizardCourse) =>
				list.some(name => item.before?.sso_course_id?.indexOf(name) !== -1);
		}

		if (this.displayOrderFilterOptions.length > MAX_FILTER_OPTIONS_ALLOWED) {
			this.displayOrderColumn!.filterFn = undefined;
			this.displayOrderColumn!.filterOptions = undefined;
		} else {
			this.displayOrderColumn!.filterOptions = this.displayOrderFilterOptions;
			this.displayOrderColumn!.filterFn = (list: number[], item: SetupWizardCourse) =>
				list.some(name => item.before?.display_order === name);
		}

		if (this.enNameFilterOptions.length > MAX_FILTER_OPTIONS_ALLOWED) {
			this.enNameColumn!.filterFn = undefined;
			this.enNameColumn!.filterOptions = undefined;
		} else {
			this.enNameColumn!.filterOptions = this.enNameFilterOptions;
			this.enNameColumn!.filterFn = (list: string[], item: SetupWizardCourse) =>
				list.some(name => item.before?.en_name?.indexOf(name) !== -1);
		}
	}

	public async initTable(): Promise<void> {
		try {
			this.initColumns();

			this.resizeSub?.unsubscribe();
			this.resizeSub = this.browserService.onResize$.subscribe((width: number) => {
				if (width >= FULL_WIDTH_BREAKPOINT) {
					this.columns.set(this.wideColumns);
				} else {
					this.columns.set(this.narrowColumns);
				}
			});
		} catch (error) {
			console.error('Error loading data:', error);
		}
	}

	private initColumns(): void {
		Object.keys(EMPTY_COURSE_CSV_TEMPLATE).map(key => {
			switch (key) {
				case CourseCsvKeys.ssoCourseId:
					this.ssoCourseIdColumn = {
						key,
						name: COURSE_CSV_LABELS[CourseCsvKeys.ssoCourseId],
						sortOrder: null,
						sortFn: (a: SetupWizardCourse, b: SetupWizardCourse) =>
							a.before?.sso_course_id!.localeCompare(b.before?.sso_course_id!) ?? -1,
						sortDirections: ['ascend', 'descend', null],
						filterMultiple: true,
					};
					break;
				case CourseCsvKeys.defaultLangId:
					this.defaultLangIdColumn = {
						key,
						name: COURSE_CSV_LABELS[CourseCsvKeys.defaultLangId],
						sortOrder: null,
						sortFn: (a: SetupWizardCourse, b: SetupWizardCourse) =>
							a.before?.default_lang_id!.localeCompare(b.before?.default_lang_id!) ?? -1,
						sortDirections: ['ascend', 'descend', null],
						filterMultiple: true,
					};
					break;
				case CourseCsvKeys.displayOrder:
					this.displayOrderColumn = {
						key,
						name: COURSE_CSV_LABELS[CourseCsvKeys.displayOrder],
						sortOrder: null,
						sortFn: (a: SetupWizardCourse, b: SetupWizardCourse) =>
							(a.before?.display_order ?? -1) - (b.before?.display_order ?? -1),
						sortDirections: ['ascend', 'descend', null],
						filterMultiple: true,
					};
					break;
				case CourseCsvKeys.rubricCode:
					this.rubricCodeColumn = {
						key,
						name: COURSE_CSV_LABELS[CourseCsvKeys.rubricCode],
						sortOrder: null,
						sortFn: (a: SetupWizardCourse, b: SetupWizardCourse) =>
							a.before?.rubric_code!.localeCompare(b.before?.rubric_code!) ?? -1,
						sortDirections: ['ascend', 'descend', null],
						filterMultiple: true,
					};
					break;
				case CourseCsvKeys.productName:
					this.productNameColumn = {
						key,
						name: COURSE_CSV_LABELS[CourseCsvKeys.productName],
						sortOrder: null,
						sortFn: (a: SetupWizardCourse, b: SetupWizardCourse) =>
							a.before?.product_name!.localeCompare(b.before?.product_name!) ?? -1,
						sortDirections: ['ascend', 'descend', null],
						filterMultiple: true,
					};
					break;
				case CourseCsvKeys.enName:
					this.enNameColumn = {
						key,
						name: COURSE_CSV_LABELS[CourseCsvKeys.enName],
						sortOrder: null,
						sortFn: (a: SetupWizardCourse, b: SetupWizardCourse) =>
							a.before?.en_name!.localeCompare(b.before?.en_name!) ?? -1,
						sortDirections: ['ascend', 'descend', null],
						filterMultiple: true,
					};
					break;
				case CourseCsvKeys.active:
					this.activeColumn = {
						key,
						name: COURSE_CSV_LABELS[CourseCsvKeys.active],
						sortOrder: null,
						// For sorting prefer the after.active property if it exists (is true or false)
						// and fall back to before.active if it is not present
						sortFn: (a: SetupWizardCourse, b: SetupWizardCourse) =>
							(a.after?.active ?? a.before?.active)
								?.toString()
								.localeCompare((b.after?.active ?? b.before?.active)?.toString()!) ?? -1,
						sortDirections: ['ascend', 'descend', null],
						filterMultiple: true,
						filterOptions: [...new Set([SetupWizardBooleanType.TRUE, SetupWizardBooleanType.FALSE])].map(
							value => ({
								text: value,
								value: value,
							})
						),
						filterFn: (list: string[], item: SetupWizardCourse) =>
							list.some(
								name => (item.after?.active ?? item.before?.active)?.toString()?.indexOf(name) !== -1
							),
					};
					break;
				default:
					break;
			}
		});

		if (
			!this.ssoCourseIdColumn ||
			!this.defaultLangIdColumn ||
			!this.displayOrderColumn ||
			!this.rubricCodeColumn ||
			!this.productNameColumn ||
			!this.enNameColumn ||
			!this.activeColumn
		) {
			throw new Error('One or more columns are not defined');
		}

		const statusColumn: Column = makeStatusColumn<SetupWizardCourse>();

		this.wideColumns = [
			this.ssoCourseIdColumn,
			this.enNameColumn,
			this.displayOrderColumn,
			this.activeColumn,
			statusColumn,
		];

		this.narrowColumns = [this.ssoCourseIdColumn, this.enNameColumn, this.activeColumn];

		this.columns.set(this.wideColumns);
	}

	onEditClick(row: SetupWizardCourse) {
		this.onEdit.emit(row);
	}

	onToggleDisableClick(row: SetupWizardCourse) {
		this.onToggleDisable.emit(row);
	}

	onRemoveClick(row: SetupWizardCourse) {
		this.onRemove.emit(row);
	}

	onResetClick(row: SetupWizardCourse) {
		this.onReset.emit(row);
	}

	ngOnDestroy() {
		this.sub?.unsubscribe();
		this.resizeSub?.unsubscribe();
	}
}
